export function isDecimalNumber(numberToCheck: number): boolean {
  return numberToCheck.toString().includes('e-');
}

export function removeTrailingZeroDigits(
  numberToConvert: number,
  decimalPrecision: number
): string {
  const [integerPart, fractionPart] = numberToConvert
    .toFixed(decimalPrecision)
    .split('.');

  // A precision of 0 leaves no fraction at all, and one that strips to nothing
  // would leave a dangling separator — "5." is not a number anything can read
  // back.
  const fraction = (fractionPart ?? '').replace(/0+$/, '');

  return fraction === '' ? integerPart : `${integerPart}.${fraction}`;
}

/**
 * The value rounded to `precision` decimals, half away from zero, with trailing
 * zeros trimmed — the same result the API gives for the same number. A magnitude
 * at or above 1e15 gives null.
 */
export function toDecimalFormat(value: number, precision = 2): string | null {
  if (!Number.isFinite(value) || Math.abs(value) >= 1e15) {
    return null;
  }

  // The decimal point is moved in the number's written form rather than by
  // multiplying, so 1.005 rounds as written and gives 1.01, as the API does:
  // `toFixed` reads it as 1.00499999999999989… and gives 1.00. A magnitude below
  // 1e-6 is written with an exponent (5e-7), so the shift is added to that one.
  const [mantissa, exponent = '0'] = String(Math.abs(value)).split('e');
  const shifted = Number(`${mantissa}e${Number(exponent) + precision}`);

  // Past 2^53 not every integer can be held, so the shifted value would land on a
  // neighbour: 123456789012345.67 would give .69. `toFixed` can't be used there
  // either, as it prints the double's binary digits: 999999999999999.9 is .88.
  if (!Number.isSafeInteger(Math.round(shifted))) {
    return roundWrittenDigits(value, precision);
  }

  const rounded = Math.round(shifted) / 10 ** precision;

  return removeTrailingZeroDigits(Math.sign(value) * rounded, precision);
}

/** As `toDecimalFormat`, on the digits of the number's shortest written form. */
function roundWrittenDigits(value: number, precision: number): string {
  const [integerDigits, fractionDigits = ''] = String(Math.abs(value)).split(
    '.'
  );
  const roundsUp = fractionDigits.charAt(precision) >= '5';
  const digits = (
    BigInt(
      integerDigits + fractionDigits.slice(0, precision).padEnd(precision, '0')
    ) + (roundsUp ? 1n : 0n)
  )
    .toString()
    .padStart(precision + 1, '0');
  const integerPart = digits.slice(0, digits.length - precision);
  const fractionPart = digits
    .slice(digits.length - precision)
    .replace(/0+$/, '');
  const sign = value < 0 ? '-' : '';

  return fractionPart
    ? `${sign}${integerPart}.${fractionPart}`
    : `${sign}${integerPart}`;
}
